import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { Modal } from "@veorest/ui";
import { formatCurrency } from "@veorest/utils";
import { Info, Radio, UserCheck, X } from "lucide-react";
import type { TranslationKey } from "../../i18n";
import { useI18n } from "../../i18n";
import {
  useAcceptPartnerOrder,
  useAssignPartnerDriver,
  useAssignPartnerOeloxDriver,
  useBlastPartnerDrivers,
  usePartnerDrivers,
  usePartnerOeloxDrivers,
  useRequestOeloxDriver,
} from "../../hooks/usePartnerOrders";
import { usePartnerProfile } from "../../hooks/usePartnerSettings";
import {
  driverPresenceTranslationKey,
  partnerAssignableDrivers,
  partnerDriverPresence,
  partnerSortDrivers,
} from "../../lib/partnerDrivers";
import {
  mapOrdersRpcError,
  type PartnerOrderFilter,
  type PartnerOrderRow,
} from "../../lib/partnerOrders";

export interface AcceptDispatchSuccess {
  filter?: PartnerOrderFilter;
  messageKey: TranslationKey;
}

interface AcceptDispatchModalProps {
  open: boolean;
  order: PartnerOrderRow | null;
  /** true = accept then dispatch; false = dispatch only (already accepted). */
  acceptFirst?: boolean;
  onClose: () => void;
  onSuccess?: (result: AcceptDispatchSuccess) => void;
}

export function AcceptDispatchModal({
  open,
  order,
  acceptFirst = true,
  onClose,
  onSuccess,
}: AcceptDispatchModalProps) {
  const { t, locale } = useI18n();
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [oeloxDriverId, setOeloxDriverId] = useState<string | null>(null);
  const [driverSearch, setDriverSearch] = useState("");
  const [busy, setBusy] = useState(false);

  const driversQuery = usePartnerDrivers();
  const profileQuery = usePartnerProfile();
  const acceptMutation = useAcceptPartnerOrder();
  const assignMutation = useAssignPartnerDriver();
  const blastMutation = useBlastPartnerDrivers();
  const oeloxMutation = useRequestOeloxDriver();
  const oeloxAssignMutation = useAssignPartnerOeloxDriver();

  /** FINAL MODEL — OELOX global drivers are a selectable optional layer. */
  const oeloxPickersEnabled =
    profileQuery.data?.oelox_fallback_enabled === true &&
    profileQuery.data?.dispatch_mode !== "bypass";
  const oeloxDriversQuery = usePartnerOeloxDrivers(oeloxPickersEnabled);

  useEffect(() => {
    if (!open) return;
    setSelectedIds(new Set());
    setOeloxDriverId(null);
    setDriverSearch("");
    setBusy(false);
  }, [open, order?.id]);

  const drivers = useMemo(
    () =>
      partnerSortDrivers(
        (driversQuery.data ?? []).filter((d) => d.is_active !== false)
      ),
    [driversQuery.data]
  );
  const visibleDrivers = useMemo(() => {
    const query = driverSearch.trim().toLowerCase();
    if (!query) return drivers;
    return drivers.filter(
      (driver) =>
        driver.name.toLowerCase().includes(query) ||
        (driver.phone ?? "").toLowerCase().includes(query)
    );
  }, [drivers, driverSearch]);
  const assignable = useMemo(
    () => partnerAssignableDrivers(drivers),
    [drivers]
  );
  /** Prefer online/busy; otherwise all partner fleet drivers. */
  const blastAllTargets = assignable.length > 0 ? assignable : drivers;

  const isBypass = profileQuery.data?.dispatch_mode === "bypass";
  const showOeloxFallback = profileQuery.data?.oelox_fallback_enabled === true;
  /** Always offer OELOX when fallback is enabled (manual dispatch). */
  const showOeloxButton = showOeloxFallback && !isBypass;

  if (!open || !order) return null;

  const title = acceptFirst
    ? t("orders.acceptModal.title")
    : t("orders.acceptModal.dispatchTitle", { business: order.tenant_name });

  const toggleDriver = (id: string) => {
    if (busy || isBypass) return;
    setOeloxDriverId(null);
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleOeloxDriver = (id: string) => {
    if (busy || isBypass) return;
    setSelectedIds(new Set());
    setOeloxDriverId((prev) => (prev === id ? null : id));
  };

  const finish = (result: AcceptDispatchSuccess) => {
    onSuccess?.(result);
    onClose();
  };

  const submit = async (opts: {
    blastAllAssignable: boolean;
    acceptOnly?: boolean;
  }) => {
    setBusy(true);
    try {
      if (acceptFirst) {
        const acceptResult = await acceptMutation.mutateAsync({
          tenantId: order.tenant_id,
          orderId: order.id,
        });
        if (acceptResult.auto_blast === true) {
          finish({
            filter: "in_delivery",
            messageKey: "orders.toast.acceptedBlast",
          });
          return;
        }
        if (acceptResult.no_drivers === true && isBypass) {
          toast.error(t("orders.acceptModal.noPartnerDrivers"));
          return;
        }
      }

      if (opts.blastAllAssignable) {
        if (blastAllTargets.length === 0) {
          toast.error(t("orders.acceptModal.noAssignableDrivers"));
          return;
        }
        await blastMutation.mutateAsync({
          tenantId: order.tenant_id,
          orderId: order.id,
          driverIds: blastAllTargets.map((d) => d.id),
        });
      } else if (selectedIds.size === 1) {
        await assignMutation.mutateAsync({
          tenantId: order.tenant_id,
          orderId: order.id,
          driverId: [...selectedIds][0]!,
        });
      } else if (selectedIds.size > 1) {
        await blastMutation.mutateAsync({
          tenantId: order.tenant_id,
          orderId: order.id,
          driverIds: [...selectedIds],
        });
      } else if (oeloxDriverId) {
        await oeloxAssignMutation.mutateAsync({
          tenantId: order.tenant_id,
          orderId: order.id,
          driverId: oeloxDriverId,
        });
      } else if (opts.acceptOnly) {
        // Accept already done; leave for manual dispatch later.
      } else if (!acceptFirst) {
        toast.error(t("orders.acceptModal.selectDriversHint"));
        return;
      }

      const messageKey: TranslationKey = oeloxDriverId
        ? "orders.toast.oeloxAssigned"
        : opts.acceptOnly
          ? "orders.toast.accepted"
          : acceptFirst
            ? "orders.toast.acceptedDispatched"
            : "orders.toast.dispatched";

      finish({
        filter: opts.acceptOnly ? "ready" : "in_delivery",
        messageKey,
      });
    } catch (err) {
      toast.error(t(mapOrdersRpcError(err)));
    } finally {
      setBusy(false);
    }
  };

  const requestOelox = async () => {
    setBusy(true);
    try {
      if (acceptFirst) {
        await acceptMutation.mutateAsync({
          tenantId: order.tenant_id,
          orderId: order.id,
        });
      }
      await oeloxMutation.mutateAsync({
        tenantId: order.tenant_id,
        orderId: order.id,
      });
      finish({
        filter: "in_delivery",
        messageKey: "orders.toast.oeloxRequested",
      });
    } catch (err) {
      toast.error(t(mapOrdersRpcError(err)));
    } finally {
      setBusy(false);
    }
  };

  const acceptBypassOnly = async () => {
    setBusy(true);
    try {
      const result = await acceptMutation.mutateAsync({
        tenantId: order.tenant_id,
        orderId: order.id,
      });
      if (result.no_drivers === true) {
        toast.error(t("orders.acceptModal.noPartnerDrivers"));
        onClose();
        return;
      }
      finish({
        filter: "in_delivery",
        messageKey: "orders.toast.acceptedBlast",
      });
    } catch (err) {
      toast.error(t(mapOrdersRpcError(err)));
    } finally {
      setBusy(false);
    }
  };

  const primaryLabel = oeloxDriverId
    ? t("orders.acceptModal.assignDriver")
    : selectedIds.size === 1
      ? acceptFirst
        ? t("orders.acceptModal.acceptAndAssign")
        : t("orders.acceptModal.assignDriver")
      : selectedIds.size > 1
        ? t("orders.acceptModal.blastSelected")
        : acceptFirst
          ? t("orders.acceptModal.acceptOnly")
          : t("orders.acceptModal.dispatch");

  /** Shared Modal shell (portal + Esc + backdrop) so the workspace never clips it. */
  return (
    <Modal
      open={open}
      onClose={() => {
        if (!busy) onClose();
      }}
      panelClassName="accept-dispatch-modal max-w-lg flex max-h-[min(92vh,720px)] flex-col"
    >
      <div className="flex items-start justify-between gap-3 border-b border-[var(--border-subtle)] px-5 py-4">
          <div className="min-w-0">
            <h2
              id="accept-dispatch-title"
              className="text-lg font-extrabold text-[var(--text-primary)]"
            >
              {title}
            </h2>
            <p className="mt-0.5 truncate text-sm text-[var(--text-secondary)]">
              {order.displayNumber} · {order.customer_name} ·{" "}
              {formatCurrency(order.total, order.currency, locale)}
            </p>
          </div>
          <button
            type="button"
            className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-[var(--border-subtle)]"
            onClick={onClose}
            disabled={busy}
            aria-label={t("common.close")}
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex-1 space-y-3 overflow-y-auto px-5 py-4">
          {isBypass && acceptFirst ? (
            <p className="text-xs font-semibold text-[var(--text-secondary)]">
              {t("orders.acceptModal.bypassModeHint")}
            </p>
          ) : null}

          <p className="accept-steps">
            <Info className="h-3.5 w-3.5 shrink-0" aria-hidden />
            {t("orders.acceptModal.steps")}
          </p>

          <div>
            <p className="mb-2 text-sm font-extrabold text-[var(--text-primary)]">
              {t("orders.acceptModal.partnerDrivers")}
            </p>
            {selectedIds.size > 0 ? (
              <p className="mb-1.5 text-xs font-bold text-[var(--accent)]">
                {t("orders.acceptModal.selectedCount", {
                  count: selectedIds.size,
                })}
              </p>
            ) : null}
            {drivers.length > 6 ? (
              <input
                className="input-base mb-2 w-full"
                value={driverSearch}
                onChange={(event) => setDriverSearch(event.target.value)}
                placeholder={t("orders.acceptModal.searchDrivers")}
                disabled={busy}
              />
            ) : null}
            {driversQuery.isLoading ? (
              <div className="h-24 rounded-xl skeleton" />
            ) : drivers.length === 0 ? (
              <p className="text-sm font-semibold text-[var(--warning,#b45309)]">
                {t("orders.acceptModal.noPartnerDrivers")}
              </p>
            ) : visibleDrivers.length === 0 ? (
              <p className="text-sm font-semibold text-[var(--text-muted)]">
                {t("orders.acceptModal.noDriverMatch")}
              </p>
            ) : (
              <ul className="space-y-1.5">
                {visibleDrivers.map((driver) => {
                  const enabled = !busy && !isBypass;
                  const selected = selectedIds.has(driver.id);
                  const presence = partnerDriverPresence(driver);
                  return (
                    <li key={driver.id}>
                      <button
                        type="button"
                        disabled={!enabled}
                        onClick={() => toggleDriver(driver.id)}
                        className={`flex min-h-[52px] w-full items-center gap-3 rounded-xl border px-3 py-3 text-start transition ${
                          selected
                            ? "border-[var(--accent)] bg-[var(--accent-soft,rgba(37,99,235,0.08))]"
                            : "border-[var(--border-subtle)] bg-transparent"
                        } ${enabled ? "" : "opacity-55"}`}
                      >
                        <span
                          className={`flex h-5 w-5 shrink-0 items-center justify-center rounded border text-[10px] font-bold ${
                            selected
                              ? "border-[var(--accent)] bg-[var(--accent)] text-white"
                              : "border-[var(--border)] text-transparent"
                          }`}
                          aria-hidden
                        >
                          ✓
                        </span>
                        <span
                          className={`accept-dot accept-dot--${presence}`}
                          aria-hidden
                        />
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm font-bold text-[var(--text-primary)]">
                            {driver.name}
                          </span>
                          <span className="block truncate text-xs text-[var(--text-muted)]">
                            {driver.phone ? `${driver.phone} · ` : ""}
                            {t(driverPresenceTranslationKey(presence))}
                            {driver.active_orders > 0
                              ? ` · ${driver.active_orders}`
                              : ""}
                          </span>
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {!isBypass && drivers.length > 0 ? (
            <div className="space-y-1">
              <button
                type="button"
                className="btn-secondary flex min-h-[44px] w-full items-center justify-center gap-2"
                disabled={busy}
                onClick={() => void submit({ blastAllAssignable: true })}
              >
                <Radio className="h-4 w-4" />
                {t("orders.acceptModal.sendToAll")}
              </button>
              <p className="text-[11px] text-[var(--text-muted)]">
                {t("orders.acceptModal.sendToAllHint")}
              </p>
            </div>
          ) : null}

          {showOeloxButton ? (
            <div className="space-y-2">
              <p className="text-sm font-extrabold text-[var(--text-primary)]">
                {t("orders.acceptModal.selectOeloxGlobal")}
              </p>
              {oeloxDriversQuery.isLoading ? (
                <div className="h-16 rounded-xl skeleton" />
              ) : (oeloxDriversQuery.data ?? []).length === 0 ? (
                <p className="text-sm font-semibold text-[var(--warning,#b45309)]">
                  {t("orders.acceptModal.oeloxNoDrivers")}
                </p>
              ) : (
                <ul className="space-y-1.5">
                  {(oeloxDriversQuery.data ?? []).map((driver) => {
                    const selected = oeloxDriverId === driver.driver_id;
                    const enabled = !busy && !isBypass;
                    return (
                      <li key={driver.driver_id}>
                        <button
                          type="button"
                          disabled={!enabled}
                          onClick={() => toggleOeloxDriver(driver.driver_id)}
                          className={`flex min-h-[52px] w-full items-center gap-3 rounded-xl border px-3 py-3 text-start transition ${
                            selected
                              ? "border-[var(--accent)] bg-[var(--accent-soft,rgba(37,99,235,0.08))]"
                              : "border-[var(--border-subtle)] bg-transparent"
                          } ${enabled ? "" : "opacity-55"}`}
                        >
                          <span className="min-w-0 flex-1">
                            <span className="block truncate text-sm font-bold text-[var(--text-primary)]">
                              {driver.name}
                            </span>
                            <span className="block truncate text-xs text-[var(--text-muted)]">
                              {driver.vehicle_number
                                ? `${driver.vehicle_number} · `
                                : ""}
                              {driver.is_online
                                ? t("orders.acceptModal.oeloxOnline")
                                : t("orders.acceptModal.oeloxOffline")}
                            </span>
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
              <button
                type="button"
                className="btn-secondary min-h-[44px] w-full"
                disabled={busy}
                onClick={() => void requestOelox()}
              >
                {t("orders.acceptModal.requestAnyOelox")}
              </button>
            </div>
          ) : null}
        </div>

        <div className="border-t border-[var(--border-subtle)] px-5 py-4">
          {isBypass && acceptFirst ? (
            <button
              type="button"
              className="btn-primary min-h-[44px] w-full"
              disabled={busy}
              onClick={() => void acceptBypassOnly()}
            >
              {t("orders.accept")}
            </button>
          ) : (
            <div className="flex gap-2">
              <button
                type="button"
                className="btn-secondary min-h-[44px] flex-1"
                disabled={busy}
                onClick={onClose}
              >
                {t("common.cancel")}
              </button>
              <button
                type="button"
                className="btn-primary flex min-h-[44px] flex-[2] items-center justify-center gap-2"
                disabled={busy}
                onClick={() =>
                  void submit({
                    blastAllAssignable: false,
                    acceptOnly: acceptFirst && selectedIds.size === 0,
                  })
                }
              >
                {selectedIds.size === 1 ? (
                  <UserCheck className="h-4 w-4" />
                ) : (
                  <Radio className="h-4 w-4" />
                )}
                {primaryLabel}
              </button>
            </div>
          )}
        </div>
    </Modal>
  );
}
